import React, { useEffect, useState } from "react";
import { CustomSelect, Button, TextInput } from "../..";
import {
  CONTENT_TYPE,
  DEFAULT_YOUTUBE_CATEGORIES,
} from "@/utils/constants/uiConstants";
import { ConditionalRules } from "@/database";

interface CustomMetaRulesProps {
  disabled?: boolean;
  isPreview?: boolean;
  readOnlyMode?: boolean;
  youtubePlaylist?: any[];
  monetizationOptions?: any[];
  bunchLanguageList?: any[];
  contentType?: any[];
  initialRules?: ConditionalRules[];
  onRulesChange?: (rules: ConditionalRules[]) => void;
  formik?: any;
}

const CustomMetaRules: React.FC<CustomMetaRulesProps> = ({
  disabled = false,
  isPreview = false,
  readOnlyMode = false,
  youtubePlaylist = [],
  monetizationOptions = [],
  bunchLanguageList = [],
  contentType = [],
  initialRules = [],
  onRulesChange,
  formik,
}) => {
  const [rules, setRules] = useState<ConditionalRules[]>(initialRules);

  // Initialize rules from formik or initialRules
  useEffect(() => {
    if (formik?.values?.conditional_rules?.length > 0) {
      setRules(formik.values.conditional_rules);
    } else if (initialRules.length > 0) {
      setRules(initialRules);
    }
  }, [formik?.values?.conditional_rules, initialRules]);

  // Update formik when rules change
  useEffect(() => {
    if (formik && rules.length >= 0) {
      formik.setFieldValue("conditional_rules", rules);
    }
    if (onRulesChange) {
      onRulesChange(rules);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules]);

  // Field options for the IF condition
  const fieldOptions = [
    { label: "Language", value: "language" },
    { label: "Content Type", value: "ctype" },
  ];

  // Operator options
  const operatorOptions = [
    { label: "Equals", value: "equals" },
    { label: "Not Equals", value: "not_equals" },
    { label: "Contains", value: "contains" },
  ];

  // Category options (Original is default)

  // Policy options (using monetization options if available)
  const policyOptions =
    monetizationOptions.length > 0
      ? [
          { label: "Original", value: "original" },
          ...monetizationOptions.map((option) => ({
            label: option.title,
            value: option.title || "",
          })),
        ]
      : [
          { label: "Original", value: "original" },
          { label: "Monetize", value: "monetize" },
          { label: "Do Not Monetize", value: "no_monetize" },
        ];

  // Playlist options
  const playlistOptions = youtubePlaylist.map((playlist) => ({
    label: playlist.title,
    value: playlist.id || "",
  }));

  const handleAddRule = () => {
    const newRule: ConditionalRules = {
      id: `rule-${Date.now()}`,
      field: "",
      operator: "equals",
      value: "",
      actions: {
        category: "original",
        monetize_policy_name: "original",
        playlist: "",
      },
    };
    setRules([...rules, newRule]);
  };

  const handleRemoveRule = (ruleId: string) => {
    const updatedRules = rules.filter((rule) => rule.id !== ruleId);
    setRules(updatedRules);
  };

  const handleRuleChange = (ruleId: string, field: string, value: any) => {
    setRules(
      rules.map((rule) => {
        if (rule.id === ruleId) {
          // Handle nested actions updates
          if (field.startsWith("actions.")) {
            const actionField = field.split(".")[1];
            return {
              ...rule,
              actions: {
                ...rule.actions,
                [actionField]: value,
              },
            };
          }
          // If changing the field type, reset the value
          if (field === "field") {
            return { ...rule, [field]: value, value: "" };
          }
          return { ...rule, [field]: value };
        }
        return rule;
      })
    );
  };

  // Get options for value field based on selected field type

  const getValueOptions = (fieldType: string) => {
    switch (fieldType) {
      case "language":
        return bunchLanguageList.map((lang: any) => ({
          label: lang.name,
          value: lang.name,
        }));
      case "ctype":
        const ctypeOptions =
          contentType.length > 0 ? contentType : CONTENT_TYPE;
        return ctypeOptions.map((ct: any) => ({
          label: ct.label,
          value: ct.value,
        }));
      default:
        return [];
    }
  };

  return (
    <div className="custom-meta-rules-container mt-3">
      {/* Header */}
      <div className="d-flex align-items-center gap-2 mb-2">
        <p
          className="mb-0 text-uppercase fw-bold"
          style={{ color: "#6b7280", fontSize: "11px" }}
        >
          Custom Meta Rules
        </p>
      </div>

      {/* Rules Container */}
      <div
        className="p-3"
        style={{
          border: "2px dashed #e5e7eb",
          borderRadius: "8px",
          backgroundColor: "#f9fafb",
        }}
      >
        {rules.length === 0 ? (
          /* Empty State */
          <div className="text-center py-3">
            <p className="text-muted mb-2" style={{ fontSize: "14px" }}>
              No rules active
            </p>
            <Button
              type="primary"
              onClick={handleAddRule}
              isDisabled={disabled || isPreview || readOnlyMode}
            >
              ADD RULE
            </Button>
          </div>
        ) : (
          /* Rules List */
          <div>
            {rules.map((rule, index) => (
              <div
                key={rule.id}
                className="mb-2 p-2 position-relative"
                style={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                }}
              >
                {/* Remove Rule Button - Top Right */}
                {!readOnlyMode && (
                  <button
                    onClick={() => handleRemoveRule(rule.id!)}
                    disabled={disabled || isPreview}
                    className="btn p-0 position-absolute d-flex align-items-center justify-content-center H"
                    style={{
                      top: "8px",
                      right: "8px",
                      width: "24px",
                      height: "24px",
                      color: "#ef4444",
                      backgroundColor: "#fee2e2",
                      fontSize: "14px",
                      lineHeight: 1,
                      opacity: disabled || isPreview ? 0.5 : 1,
                      cursor: disabled || isPreview ? "not-allowed" : "pointer",
                      border: "1px solid #fecaca",
                      borderRadius: "50%",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      if (!disabled && !isPreview) {
                        e.currentTarget.style.backgroundColor = "#ef4444";
                        e.currentTarget.style.color = "#ffffff";
                        e.currentTarget.style.borderColor = "#ef4444";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#fee2e2";
                      e.currentTarget.style.color = "#ef4444";
                      e.currentTarget.style.borderColor = "#fecaca";
                    }}
                  >
                    <i className="bi bi-x-lg H"></i>
                  </button>
                )}

                <div className="row align-items-start">
                  {/* Drag Handle */}
                  <div className="col-auto d-flex flex-column align-items-center justify-content-start pt-3">
                    <div
                      className="mb-1"
                      style={{
                        cursor: "grab",
                        color: "#9ca3af",
                        fontSize: "12px",
                      }}
                    >
                      <i className="bi bi-grip-vertical"></i>
                      <i className="bi bi-grip-vertical"></i>
                    </div>
                    <span
                      className="badge rounded-pill"
                      style={{
                        backgroundColor: "#f3f4f6",
                        color: "#6b7280",
                        fontSize: "9px",
                        padding: "3px 6px",
                      }}
                    >
                      #{index + 1}
                    </span>
                  </div>

                  {/* IF Section */}
                  <div className="col-12 col-md-3">
                    <div className="mb-2">
                      <span
                        className="badge mb-2"
                        style={{
                          backgroundColor: "#dbeafe",
                          color: "#3b82f6",
                          fontSize: "11px",
                          fontWeight: 600,
                          padding: "4px 8px",
                        }}
                      >
                        IF
                      </span>
                      <div className="d-flex flex-column gap-2">
                        <div>
                          <CustomSelect
                            value={rule.field || ""}
                            placeholder="Select field"
                            options={fieldOptions}
                            handleChange={(newValue: any) => {
                              handleRuleChange(
                                rule.id!,
                                "field",
                                newValue.value
                              );
                            }}
                            // disabled={disabled || isPreview || readOnlyMode}
                            disabled={true}
                            showRequired={false}
                          />
                        </div>
                        <div>
                          {rule.field === "language" ||
                          rule.field === "ctype" ? (
                            <CustomSelect
                              value={rule.value || ""}
                              placeholder={
                                rule.field === "language"
                                  ? "Select language"
                                  : "Select content type"
                              }
                              options={getValueOptions(rule.field)}
                              handleChange={(newValue: any) => {
                                handleRuleChange(
                                  rule.id!,
                                  "value",
                                  newValue.value
                                );
                              }}
                              // disabled={
                              //   !rule.field ||
                              //   disabled ||
                              //   isPreview ||
                              //   readOnlyMode
                              // }
                              disabled={true}
                              showRequired={false}
                            />
                          ) : (
                            <TextInput
                              value={rule.value || ""}
                              onChange={(e) => {
                                handleRuleChange(
                                  rule.id!,
                                  "value",
                                  e.target.value
                                );
                              }}
                              placeholder="Enter value"
                              disabled={
                                !rule.field ||
                                disabled ||
                                isPreview ||
                                readOnlyMode
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="col-auto d-flex align-items-center justify-content-center pt-4">
                    <i
                      className="bi bi-arrow-right"
                      style={{ color: "#9ca3af", fontSize: "18px" }}
                    ></i>
                  </div>

                  {/* THEN Section */}
                  <div className="col">
                    <div>
                      <span
                        className="badge mb-2"
                        style={{
                          backgroundColor: "#d1fae5",
                          color: "#10b981",
                          fontSize: "11px",
                          fontWeight: 600,
                          padding: "4px 8px",
                        }}
                      >
                        THEN
                      </span>
                      <div className="row g-2">
                        <div className="col-12 col-md-6">
                          <div className="d-flex align-items-center gap-1 mb-1">
                            <i
                              className="bi bi-list"
                              style={{ color: "#9ca3af", fontSize: "12px" }}
                            ></i>
                            <label
                              className="form-label mb-0"
                              style={{ fontSize: "11px", color: "#6b7280" }}
                            >
                              CATEGORY
                            </label>
                          </div>
                          <CustomSelect
                            value={rule.actions?.category || "original"}
                            options={Object.entries(
                              DEFAULT_YOUTUBE_CATEGORIES
                            ).map(([key, value]) => ({
                              label: value,
                              value: key,
                            }))}
                            handleChange={(newValue: any) => {
                              handleRuleChange(
                                rule.id!,
                                "actions.category",
                                newValue.value
                              );
                            }}
                            // disabled={disabled || isPreview || readOnlyMode}
                            disabled={true}
                            showRequired={false}
                          />
                        </div>
                        <div className="col-12 col-md-6">
                          <div className="d-flex align-items-center gap-1 mb-1">
                            <i
                              className="bi bi-shield-check"
                              style={{ color: "#9ca3af", fontSize: "12px" }}
                            ></i>
                            <label
                              className="form-label mb-0"
                              style={{ fontSize: "11px", color: "#6b7280" }}
                            >
                              POLICY
                            </label>
                          </div>
                          <CustomSelect
                            value={
                              rule.actions?.monetize_policy_name || "original"
                            }
                            options={policyOptions}
                            handleChange={(newValue: any) => {
                              handleRuleChange(
                                rule.id!,
                                "actions.monetize_policy_name",
                                newValue.value
                              );
                            }}
                            // disabled={disabled || isPreview || readOnlyMode}
                            disabled={true}
                            showRequired={false}
                          />
                        </div>
                        <div className="col-12">
                          <div className="d-flex align-items-center gap-1 mb-1">
                            <i
                              className="bi bi-collection-play"
                              style={{ color: "#9ca3af", fontSize: "12px" }}
                            ></i>
                            <label
                              className="form-label mb-0"
                              style={{ fontSize: "11px", color: "#6b7280" }}
                            >
                              TARGET PLAYLIST
                            </label>
                          </div>
                          <CustomSelect
                            value={
                              rule.actions?.playlist
                                ? rule.actions.playlist.split(",")
                                : []
                            }
                            placeholder="Select playlists..."
                            options={playlistOptions}
                            handleChange={(newValue: any) => {
                              const selectedValues = newValue
                                .map((v: any) => v.value)
                                .join(",");
                              handleRuleChange(
                                rule.id!,
                                "actions.playlist",
                                selectedValues
                              );
                            }}
                            // disabled={disabled || isPreview || readOnlyMode}
                            disabled={true}
                            showRequired={false}
                            multiSelect={true}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Add Rule Button */}
            {/* {!readOnlyMode && (
              <div className="text-center mt-2 H">
                <Button
                  type="outline-primary"
                  onClick={handleAddRule}
                  isDisabled={disabled || isPreview}
                  icon={<i className="bi bi-plus-lg"></i>}
                >
                  ADD RULE
                </Button>
              </div>
            )} */}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomMetaRules;
